import { useQuery } from '@tanstack/react-query'
import { compact, isEmpty, uniqBy } from 'lodash'
import moment from 'moment'
import { useMemo, useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { useTransientEventBadge } from '../../hooks/useTransientEventBadge'
import { Availability } from '../../models/Availability'
import { isFixedEventReservationItem } from '../../models/Bookable'
import { Booking } from '../../models/Booking'
import { Field } from '../../models/Field'
import { Item } from '../../models/Item'
import { Job } from '../../models/Job'
import { Line } from '../../models/Line'
import { Package } from '../../models/Package'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { apiClient } from '../../modules/apiclient'
import { isPast } from '../../utils/dates'
import { getIconNameFromType } from '../../utils/item'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { AddressPicker } from '../common/AddressPicker'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { ContractSearchableSelect } from '../items/ContractSearchableSelect'
import { TypeSearchableSelect } from '../items/TypeSearchableSelect'
import { ImageView } from '../common/ImageView'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { Chip } from '../common/fields/Chip'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { CustomFieldRecordModal } from '../items/CustomFieldRecordModal'
import { Receipt, WorkflowPage } from '../workflows/WorkflowPage'
import { AvailabilityModal } from './AvailabilityModal'
import { ScheduleModal } from './ScheduleModal'

type Props = {
    booking: Booking
    owner: User
    pros: User[]
    items: Item[]
    fields: Field[]
    zoomConnected: boolean
    onUpdateBooking: (updatedBooking: Booking, itemId?: string) => void
    loadingItemsMap: Record<string, boolean>
    isSyncingItemBooking?: boolean
    availablePackages?: RecordDataMap
}

export const NewBookingPage = ({
    booking,
    owner,
    pros,
    items,
    fields,
    zoomConnected,
    onUpdateBooking,
    loadingItemsMap,
    isSyncingItemBooking = false,
    availablePackages,
}: Props) => {
    const { logAndDisplayError } = useErrorHandler()

    const [activeModal, setActiveModal] = useState<
        undefined | 'availability' | 'schedule' | 'customFields'
    >(undefined)

    const [guardrail, setGuardrail] = useState<
        { type: 'doubleBook'; pro: User } | { type: 'incompatibleServices'; item: Item } | null
    >(null)

    const { data: employeeAvailability, isLoading: searchingEmployeeAvailability } = useQuery({
        queryKey: [
            'availability',
            booking.id,
            booking.date?.toISOString(),
            booking.item?.id,
            booking.location,
            booking.duration,
            booking.vendor?.id,
        ],
        queryFn: async () => {
            const { date, item, location, duration, vendor } = booking
            const result = await RecordFactory.getInstance().searchEmployeeAvailability(
                booking,
                date || moment(),
                undefined,
                item,
                undefined,
                location,
                duration,
                vendor,
                false
            )
            if (!result.ok) throw result.error

            const availabilities = new RecordDataMap()

            if (result.value.length) {
                for (const data of result.value) {
                    if (!data.hasValueForKey('availability')) continue

                    for (const availability of data.valueForRecordListKey<Availability>(
                        'availability',
                        Availability
                    )) {
                        for (const staff of availability.staffing) {
                            if (!staff.person?.id) continue
                            if (!availabilities.hasValueForKey(staff.person.id))
                                availabilities.put(staff.person.id, [])
                            availabilities.get(staff.person.id).push(availability)
                        }
                    }
                }
            }

            return availabilities
        },
        onError: (error) => {
            logAndDisplayError(error)
        },
    })

    const handleSelectClient = (client: User | undefined) => {
        const updatedBooking = booking.clone({ client, subaccount: undefined })
        onUpdateBooking(updatedBooking)
    }

    const handleSelectSubaccount = (subaccount: Subaccount | undefined) => {
        const updatedBooking = booking.clone({ client: subaccount?.client, subaccount })
        onUpdateBooking(updatedBooking)
    }

    const selectedPros = useMemo(() => {
        return uniqBy(compact(booking.jobs.map((job) => job.person)), (user) => user.id)
    }, [booking.jobs])

    const proChips = useMemo(() => {
        return compact(
            selectedPros.map((pro) => {
                if (!pro.id || !pro.name) return null
                return { id: pro.id, title: pro.name }
            })
        )
    }, [selectedPros])

    const proSearchableItems = [
        ...(pros ?? []).map((pro) => ({
            id: pro.id ?? '',
            title: pro.name ?? '',
            subtitle: !searchingEmployeeAvailability ? (
                employeeAvailability?.hasValueForKey(pro.id ?? '') ? (
                    <span className="text-success-400">Available</span>
                ) : (
                    <span className="text-danger-400">Unavailable</span>
                )
            ) : (
                <Spinner size={16} />
            ),
            tertiaryView:
                pro.image?.uri !== undefined ? (
                    <img
                        src={pro.image.uri}
                        className="size-9 rounded-full border-2 border-white"
                    />
                ) : (
                    <Icon icon="user.small" background="round" className="size-9 shrink-0" />
                ),
            isChecked: selectedPros.map((p) => p.id).includes(pro.id ?? ''),
            pro,
        })),
    ]

    const handleSelectPro = (pro: User | undefined) => {
        if (!pro?.id || !employeeAvailability) return

        const isSelected = selectedPros.map((p) => p.id).includes(pro.id)
        const available = employeeAvailability.hasValueForKey(pro.id)

        if (!isSelected && !available) {
            setGuardrail({ type: 'doubleBook', pro })
        }
        else {
            const updatedPros = isSelected
                ? selectedPros.filter((p) => p.id !== pro.id)
                : [...selectedPros, pro]

            updateSelectedPros(updatedPros)
        }
    }

    const updateSelectedPros = (pros: User[]) => {
        onUpdateBooking(
            booking.clone({
                jobs: pros.map((p) => Job.initWithPerson(p)),
                employee:
                    pros.length > 0
                        ? pros.find((p) => p.perm >= User.PERM_ROLE_EMPLOYEE)
                        : undefined,
            })
        )
    }

    const { isBadgeShowing: isProductAddedBadgeShowing, onEvent: onProductAdded } =
        useTransientEventBadge()

    const nonServices = useMemo(
        () => booking.serviceLines.filter((line) => line.item?.type !== 'service'),
        [booking.serviceLines]
    )

    const qualifiesPrepaidPackage = (item: Item | undefined) => {
        return booking.owner?.allowPackage && availablePackages?.hasValueForKey(item?.id ?? '')
    }

    const itemsSearchableItems = useMemo(() => {
        return items
            .filter((item) => !item.endDate || !isPast(item.endDate))
            .map((item) => ({
                id: item.id ?? '',
                type: item.type ?? '',
                title: item.name ?? '',
                subtitle: qualifiesPrepaidPackage(item)
                    ? 'Prepaid package'
                    : currencyFormat(item.rate),
                tertiaryView: (
                    <ImageView
                        fallbackIcon={{
                            icon: `${getIconNameFromType(item.type ?? '')}.small`,
                            background: 'round',
                            className: 'size-9',
                        }}
                        className="size-9 rounded-full object-cover"
                        image={item.image}
                    />
                ),
                isChecked:
                    owner.allowServices || item.type === 'product'
                        ? booking.lines.map((line) => line.item?.id).includes(item.id)
                        : undefined,
                isLoading: !!item.id && loadingItemsMap[item.id],
                isDisabled: isSyncingItemBooking,
                item,
            }))
    }, [
        items,
        booking.lines,
        booking.owner,
        availablePackages,
        loadingItemsMap,
        isSyncingItemBooking,
    ])

    const handleSelectServiceOption = async (
        itemOption: (typeof itemsSearchableItems)[number] | null
    ) => {
        if (!itemOption || itemOption.isLoading || isSyncingItemBooking) return

        const item = itemOption.item

        if (itemOption.isChecked) {
            removeServiceLine(booking.serviceLines.find((line) => line.item?.id === itemOption.id))
        }
        else {
            if (nonServices.length > 0 && nonServices[0].item) {
                setGuardrail({ type: 'incompatibleServices', item: nonServices[0].item })
            }
            else if (booking.serviceLines.length > 0 && item.type !== 'service') {
                setGuardrail({ type: 'incompatibleServices', item })
            }
            else {
                const line = Line.initWithItem(item, booking)

                if (
                    item?.type === 'reservation' &&
                    !item.duration &&
                    item.rate &&
                    (booking.duration ?? 0) >= 1440
                ) {
                    line.quantity = (booking.duration ?? 0) / 1440
                    line.total = item.rate * line.quantity - (item.deposit ?? 0)
                    line.rate = line.total / line.quantity
                }

                const updatedBooking = booking.clone({
                    lines: [...booking.lines, line],
                    item:
                        booking.serviceLines.length > 0 ? booking.serviceLines[0].item : line.item,
                })

                onUpdateBooking(updatedBooking, itemOption.id)
            }
        }
    }

    const handleUpdateServiceLineAtIndex = (index: number, newLine: Line) => {
        const lineToUpdate = booking.serviceLines[index]
        if (!lineToUpdate) return

        onUpdateBooking(
            booking.clone({
                lines: booking.lines.map((line) => (line === lineToUpdate ? newLine : line)),
            }),
            lineToUpdate.item?.id
        )
    }

    const handleRemoveServiceLineAtIndex = (index: number) => {
        removeServiceLine(booking.serviceLines[index])
    }

    const removeServiceLine = (lineToRemove: Line | undefined) => {
        if (!lineToRemove) return

        const serviceLines = booking.serviceLines.filter((line) => line !== lineToRemove)

        onUpdateBooking(
            booking.clone({
                lines: booking.lines.filter((line) => line !== lineToRemove),
                item: serviceLines.length > 0 ? serviceLines[0].item : undefined,
            }),
            lineToRemove.item?.id
        )
    }

    const handleSelectProductOption = async (
        itemOption: (typeof itemsSearchableItems)[number] | null
    ) => {
        if (!itemOption || itemOption.isLoading || isSyncingItemBooking) return

        if (itemOption.isChecked) {
            removeProductLine(booking.productLines.find((line) => line.item?.id === itemOption.id))
        }
        else {
            const line = Line.initWithItem(itemOption.item, booking)
            const updatedBooking = booking.clone({
                lines: [...booking.lines, line],
                product: booking.productLines.length > 0 ? booking.productLines[0].item : line.item,
            })

            onUpdateBooking(updatedBooking, itemOption.id)
            onProductAdded()
        }
    }

    const handleUpdateProductLineAtIndex = (index: number, newLine: Line) => {
        const lineToUpdate = booking.productLines[index]
        if (!lineToUpdate) return

        onUpdateBooking(
            booking.clone({
                lines: booking.lines.map((line) => (line === lineToUpdate ? newLine : line)),
            }),
            lineToUpdate.item?.id
        )
    }

    const handleRemoveProductLineAtIndex = (index: number) => {
        removeProductLine(booking.productLines[index])
    }

    const removeProductLine = (lineToRemove: Line | undefined) => {
        if (!lineToRemove) return

        const productLines = booking.productLines.filter((line) => line !== lineToRemove)

        onUpdateBooking(
            booking.clone({
                lines: booking.lines.filter((line) => line !== lineToRemove),
                product: productLines.length > 0 ? productLines[0].item : undefined,
            }),
            lineToRemove.item?.id
        )
    }

    const confirmStatusStrings = useMemo(() => {
        return User.confirmStatusStrings().map((option) => ({
            ...option,
            disabled: !!booking.deposit && option.id !== 'confirmPayment',
        }))
    }, [booking.deposit])

    const selectedConfirmStatusId = useMemo(() => {
        return booking.confirmedDate || booking.status === 'confirmed'
            ? 'confirmed'
            : booking.confirmStatus === 'confirmPayment'
              ? 'confirmPayment'
              : 'confirmOnly'
    }, [booking.confirmedDate, booking.status, booking.confirmStatus])

    const prepaidPackage = useMemo(() => {
        return availablePackages?.valueForRecordKey<Package>(
            booking.services.find((item) => qualifiesPrepaidPackage(item))?.id ?? '',
            Package
        )
    }, [availablePackages, booking.services, qualifiesPrepaidPackage])

    const receipt = useMemo((): Receipt => {
        const forClass = booking.item?.type === 'class'
        return {
            modifiers: {
                subtotal:
                    !forClass || booking.dropin
                        ? {
                              amount: booking.total ?? 0,
                              title: booking.forSchedule ? 'Recurring amount' : undefined,
                              subtitle:
                                  booking.forSchedule && booking.schedule?.instances
                                      ? `Total ${currencyFormat((booking.total ?? 0) * booking.schedule.instances)} (${booking.schedule.instances} sessions)`
                                      : undefined,
                              onUpdate: (total) => {
                                  onUpdateBooking(
                                      booking.clone({
                                          rate: Math.max(total - (booking.deposit ?? 0), 0),
                                      })
                                  )
                              },
                          }
                        : undefined,
                deposit: {
                    amount: booking.deposit !== undefined ? booking.deposit : null,
                    title: forClass && !booking.dropin ? 'Amount due' : undefined,
                    subtitle:
                        forClass && !booking.dropin
                            ? 'Due upfront when the client confirms this class'
                            : undefined,
                    isPaid: !!booking.deposit && !!booking.paidDepositDate,
                    onChange: (deposit) => {
                        onUpdateBooking(
                            booking.clone({
                                deposit,
                                rate:
                                    !forClass || booking.dropin
                                        ? Math.max((booking.total ?? 0) - (deposit ?? 0), 0)
                                        : 0,
                                confirm_status: !deposit ? booking.confirmStatus : 'confirmPayment',
                                status: !deposit ? booking.status : 'sentQuote',
                            })
                        )
                    },
                },
                package: owner.allowPackage ? prepaidPackage : undefined,
                giftCertificate: owner.allowGift
                    ? {
                          selectedGift: booking.gift,
                          onSelectGift: (gift) => {
                              if (!!gift) {
                                  onUpdateBooking(
                                      booking.clone({
                                          gift,
                                          discount_type: 'rate',
                                          discount: gift.balance ?? 0,
                                      })
                                  )
                              }
                            else {
                                  onUpdateBooking(booking.clone({ gift: undefined }))
                              }
                          },
                      }
                    : undefined,
                discount: owner.allowDiscount ? {} : undefined,
                salesTax: owner.allowSalesTax
                    ? {
                          amount: booking.taxAmount ?? null,
                          parenthetical: !!booking.taxcode ? `${booking.taxcode.rate ?? 0}%` : null,
                      }
                    : undefined,
                surcharge:
                    owner.allowSurcharge && (!forClass || booking.dropin)
                        ? {
                              amount: booking.allowSurcharge ? booking.surchargeAmount : null,
                              onAdd: () =>
                                  onUpdateBooking(booking.clone({ allow_surcharge: true })),
                              onRemove: () =>
                                  onUpdateBooking(booking.clone({ allow_surcharge: false })),
                          }
                        : undefined,
                deleteButtonMargin: '-right-15.5',
            },
            total: booking.paidDate ? booking.chargeAmount : booking.finalChargeAmount,
        }
    }, [booking, prepaidPackage, owner])

    const videoConferenceOptions = useMemo(() => {
        return Item.videoConferenceStringsSync('class' === booking.type, zoomConnected).filter(
            (option) => option.enabled
        )
    }, [booking.type, zoomConnected])

    const handleSelectAddress = async (address: string) => {
        const { data } = await apiClient.post('/location/parse', { q: address })
        if (!data) return

        const { line1, line2, city, state, postal_code, country_code } = data

        onUpdateBooking(
            booking.clone({
                line1,
                line2,
                city,
                state,
                postal_code,
                country_code,
            })
        )
    }

    return (
        <div className="mt-12 flex min-w-[744px] flex-col items-center justify-start gap-y-6">
            <WorkflowPage.Card title="General">
                <WorkflowPage.IconSection
                    title={
                        owner.allowSubaccount
                            ? `Client / ${Subaccount.labelString(
                                  owner.industry?.subaccountType ?? 'none',
                                  false
                              )} (required)`
                            : 'Client (required)'
                    }
                    icon="user.large"
                >
                    <ClientSearchableSelect
                        mode="server"
                        selectedClient={booking.client}
                        selectedSubaccount={booking.subaccount}
                        onSelectClient={handleSelectClient}
                        onSelectSubaccount={handleSelectSubaccount}
                        shouldPreserveLastSelection={true}
                        shouldIncludeSubaccounts
                        canAddNew
                    />
                </WorkflowPage.IconSection>

                {owner.allowStaffing && PSApplication.me().perm > User.PERM_ROLE_EMPLOYEE && (
                    <WorkflowPage.IconSection title="Pros" icon="employeeCard.large">
                        <SearchableSelect
                            placeholder="Type to search"
                            items={proSearchableItems}
                            chips={proChips}
                            onSelectItem={(item) => handleSelectPro(item?.pro)}
                            onRemoveChipWithID={(chipId) =>
                                updateSelectedPros(selectedPros.filter((p) => chipId !== p.id))
                            }
                            disabled={isSyncingItemBooking}
                            shouldRefocusAfterSelection
                        />
                    </WorkflowPage.IconSection>
                )}

                <WorkflowPage.IconSection
                    title={owner.allowServices ? 'Services' : 'Service'}
                    icon="tag.large"
                    secondaryTextClassName="font-medium text-success-400"
                >
                    <SearchableSelect
                        titleWeight="bold"
                        placeholder="Type to search"
                        items={itemsSearchableItems.filter(
                            (itemOption) => itemOption.type !== 'product'
                        )}
                        onSelectItem={handleSelectServiceOption}
                        disabled={!owner.allowServices && booking.serviceLines.length === 1}
                        shouldRefocusAfterSelection
                    />
                    {booking.serviceLines.map((line, lineIndex) => (
                        <WorkflowPage.ItemLine
                            key={line.item?.id ?? lineIndex}
                            line={line}
                            onUpdate={(newLine) =>
                                handleUpdateServiceLineAtIndex(lineIndex, newLine)
                            }
                            onRemove={() => handleRemoveServiceLineAtIndex(lineIndex)}
                            secondaryTitle={
                                qualifiesPrepaidPackage(line.item) ? 'Prepaid' : undefined
                            }
                            disabled={isSyncingItemBooking}
                            shownFields={['salesTax']}
                        />
                    ))}
                </WorkflowPage.IconSection>

                {owner.allowProduct && (
                    <WorkflowPage.IconSection
                        title="Products"
                        icon="package.large"
                        secondaryText={isProductAddedBadgeShowing ? 'Added' : undefined}
                        secondaryTextClassName="font-medium text-success-400"
                    >
                        <SearchableSelect
                            titleWeight="bold"
                            placeholder="Type to search"
                            items={itemsSearchableItems.filter(
                                (itemOption) => itemOption.type === 'product'
                            )}
                            onSelectItem={handleSelectProductOption}
                            shouldRefocusAfterSelection
                        />

                        {booking.productLines.map((line, lineIndex) => (
                            <WorkflowPage.ItemLine
                                key={line.item?.id ?? lineIndex}
                                line={line}
                                onUpdate={(newLine) =>
                                    handleUpdateProductLineAtIndex(lineIndex, newLine)
                                }
                                onRemove={() => handleRemoveProductLineAtIndex(lineIndex)}
                                disabled={isSyncingItemBooking}
                                shownFields={['quantity', 'salesTax']}
                                showRate
                            />
                        ))}
                    </WorkflowPage.IconSection>
                )}
            </WorkflowPage.Card>

            <WorkflowPage.Card title="Details">
                <WorkflowPage.IconSection
                    title="Date (required)"
                    icon="calendar2.large"
                    thirdLine={
                        <div className="text-secondary-400 font-normal">
                            {booking.date?.format('MMMM D, YYYY h:mm A')}
                        </div>
                    }
                    secondaryView={
                        <Button
                            size="small"
                            intent="tertiaryLink"
                            className="mr-2 translate-x-3 hover:opacity-100"
                            onClick={() => setActiveModal('availability')}
                        >
                            {!!booking.schedule ? 'Edit' : 'Select'}
                        </Button>
                    }
                    hasHoverEffect
                />

                <WorkflowPage.IconSection
                    title="Status"
                    icon="windowClock.large"
                    description="Select the appointment confirmation status"
                >
                    <div className="flex flex-wrap items-center justify-start gap-2">
                        {confirmStatusStrings.map(({ id, label, disabled }, index) => (
                            <Chip
                                key={id ?? label ?? index}
                                size="small"
                                title={label ?? ''}
                                isSelected={id === selectedConfirmStatusId}
                                onClick={() =>
                                    onUpdateBooking(
                                        booking.clone({
                                            confirm_status: id === 'confirmed' ? '' : id,
                                            status: id !== 'confirmed' ? 'sentQuote' : id,
                                            channel:
                                                id !== 'confirmPayment' && !booking.channel
                                                    ? !booking.client || !!booking.client.e164
                                                        ? 'sms'
                                                        : 'email'
                                                    : booking.channel,
                                        })
                                    )
                                }
                                disabled={disabled}
                            />
                        ))}
                    </div>
                </WorkflowPage.IconSection>

                {owner.allowRecurring &&
                    (!booking.id || booking.forSchedule) &&
                    (booking.item?.type !== 'class' || booking.dropin) &&
                    (!booking.item || !isFixedEventReservationItem(booking.item)) && (
                        <WorkflowPage.IconSection
                            title="Repeat"
                            icon="repeat.large"
                            description={
                                !booking.schedule ? 'Select a recurring schedule' : undefined
                            }
                            thirdLine={
                                !!booking.schedule && (
                                    <div className="text-secondary-400 font-normal">
                                        {booking.schedule.description}
                                    </div>
                                )
                            }
                            secondaryView={
                                <Button
                                    size="small"
                                    intent="tertiaryLink"
                                    className="mr-2 translate-x-3 hover:opacity-100"
                                    onClick={() => setActiveModal('schedule')}
                                >
                                    {!!booking.schedule ? 'Edit' : 'Select'}
                                </Button>
                            }
                            hasHoverEffect
                        />
                    )}
            </WorkflowPage.Card>

            <WorkflowPage.Card title="Total">
                <WorkflowPage.ReceiptView
                    receipt={receipt}
                    transaction={booking}
                    onUpdateTransaction={onUpdateBooking}
                    client={booking.client}
                    owner={owner}
                />
            </WorkflowPage.Card>

            <WorkflowPage.BookingNotificationsSection
                booking={booking}
                onUpdateBooking={onUpdateBooking}
            />

            <WorkflowPage.Card title="More options" defaultIsCollapsed>
                <WorkflowPage.IconSection
                    title="Add video conference"
                    icon="video.large"
                    description="Add a video conference link to this appointment"
                >
                    <div className="flex flex-wrap items-center justify-start gap-2">
                        {videoConferenceOptions.map(({ id, label, subtitle }, index) => (
                            <Chip
                                key={id ?? label ?? index}
                                size="small"
                                title={subtitle ?? label ?? ''}
                                isSelected={(booking.conferenceType ?? '') === (id ?? '')}
                                onClick={() =>
                                    onUpdateBooking(booking.clone({ conference_type: id }))
                                }
                            />
                        ))}
                    </div>
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection title="Location" icon="markerPin.large">
                    <AddressPicker
                        size="medium"
                        placeholder="Add a location"
                        defaultAddress={booking.getLocation()}
                        onSelect={handleSelectAddress}
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Description"
                    icon="note.large"
                    secondaryText={`${(booking.memo ?? '').length} / 900`}
                >
                    <TextArea
                        size="medium"
                        isAutoResizing
                        placeholder="Enter a description"
                        className="overlow-y-hidden"
                        value={booking.memo ?? ''}
                        onChange={(event) =>
                            onUpdateBooking(booking.clone({ memo: event.target.value }))
                        }
                        maxLength={900}
                        onClear={() => onUpdateBooking(booking.clone({ memo: '' }))}
                    />
                </WorkflowPage.IconSection>

                {fields.length > 0 && (
                    <WorkflowPage.IconSection
                        title="More details"
                        icon="form2.large"
                        thirdLine={
                            <div className="text-secondary-400 font-normal">
                                {!isEmpty(booking.fieldValues)
                                    ? Field.valuesAsString(
                                          Field.loadFieldData(fields, booking.fieldValues)
                                      )
                                    : undefined}
                            </div>
                        }
                        secondaryView={
                            <Button
                                size="small"
                                intent="tertiaryLink"
                                className="mr-2 translate-x-3 hover:opacity-100"
                                onClick={() => setActiveModal('customFields')}
                            >
                                {!isEmpty(booking.fieldValues) ? 'Edit' : 'Select'}
                            </Button>
                        }
                        hasHoverEffect
                    />
                )}

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a form" icon="field.large">
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={booking.form}
                            onSelectForm={(form) => onUpdateBooking(booking.clone({ form }))}
                        />
                    </WorkflowPage.IconSection>
                )}

                {owner.allowContract && (
                    <WorkflowPage.IconSection title="Attach a contract" icon="pen.large">
                        <ContractSearchableSelect
                            selectedContract={booking.contract}
                            onSelectContract={(contract) =>
                                onUpdateBooking(booking.clone({ contract }))
                            }
                        />
                    </WorkflowPage.IconSection>
                )}
            </WorkflowPage.Card>

            {activeModal === 'availability' && (
                <AvailabilityModal
                    isOpen={activeModal === 'availability'}
                    booking={booking.clone()}
                    onClose={() => setActiveModal(undefined)}
                    onDone={(record: Booking) => {
                        onUpdateBooking(
                            booking.clone({
                                date: record.date,
                                dates: record.dates,
                                end_date: record.endDate,
                                duration: record.duration,
                                schedule: record.schedule,
                                is_dropin: record.dropin,
                                lesson: record.lesson,
                                lessons: record.lessons,
                                lines: record.lines,
                                rate: record.rate,
                            })
                        )
                    }}
                />
            )}

            {activeModal === 'schedule' && (
                <ScheduleModal
                    isOpen={activeModal == 'schedule'}
                    booking={booking}
                    onClose={() => setActiveModal(undefined)}
                    onDone={(schedule) => {
                        onUpdateBooking(
                            booking.clone({
                                schedule,
                                for_schedule: !!schedule,
                            })
                        )
                    }}
                />
            )}

            {activeModal === 'customFields' && (
                <CustomFieldRecordModal
                    isOpen={activeModal === 'customFields'}
                    title="More details"
                    fields={fields}
                    answers={booking.fieldValues}
                    onUpdateAnswers={(answers) =>
                        onUpdateBooking(booking.clone({ field_values: new RecordDataMap(answers) }))
                    }
                    onClose={() => setActiveModal(undefined)}
                    shouldEnforceRequired
                />
            )}

            {guardrail?.type === 'doubleBook' && (
                <ConfirmationModal
                    isOpen
                    title="Double book?"
                    confirmButtonText="Yes, double book"
                    onClose={() => setGuardrail(null)}
                    onConfirm={() => {
                        if (guardrail.pro) updateSelectedPros([...selectedPros, guardrail.pro])
                        setGuardrail(null)
                    }}
                    shouldHideCancelButton
                >
                    {guardrail.pro && (
                        <Paragraph>
                            {guardrail.pro.name ?? 'This pro'} is unavailable, do you want to double
                            book them?
                        </Paragraph>
                    )}
                </ConfirmationModal>
            )}

            {guardrail?.type === 'incompatibleServices' && (
                <ConfirmationModal
                    isOpen
                    title="Incompatible services"
                    confirmButtonText="OK"
                    onClose={() => setGuardrail(null)}
                    onConfirm={() => setGuardrail(null)}
                    shouldHideCancelButton
                >
                    {guardrail.item && (
                        <Paragraph>
                            {guardrail.item.name ?? 'This service'} cannot be scheduled with other
                            services
                        </Paragraph>
                    )}
                </ConfirmationModal>
            )}
        </div>
    )
}
